/*** IMPORTS FROM imports-loader ***/
var define = false;

/*jslint browser:true, todo: true */
"use strict";

var ko = require("knockout");
var R = require("ramda");

var pli = require("../forms");

// load knockout binding as a side-effect
require("../ko-grid");



//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// "PicklistForm" class
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

/**
 * Create a new instance of picklist form, upgrading the specified original picklist form.
 *
 * PicklistForm is a singleton, so this constructor will only be called once (during initial
 * JavaScript parsing and execution). This also means that using PicklistForm.prototype in
 * this constructor is ok.
 *
 * @param {Object} originalForm
 *      The picklist form registered in pli.forms.registry
 */
function PicklistForm(originalForm) {
    // validate original form
    if (typeof originalForm !== "object") { throw new Error("Form picklistForm has not been registered in Pli.forms.registry (did you call CreatePicklistForm?)"); }
    if (originalForm.type !== "PLI navigation form") { throw new Error("Form picklistForm has already been upgraded by PicklistForm.js"); }

    // preserve original form's methods and properties
    PicklistForm.prototype._originalOpen = originalForm.open;
    PicklistForm.prototype._originalClose = originalForm.close;
    ko.utils.extend(this, originalForm);
    this.page.form = this; // re-link navigation page to this form (was pointing to "originalForm" before assignment)

    // bind all prototype methods to this singleton instance
    var k;
    for (k in PicklistForm.prototype) {
        if (PicklistForm.prototype.hasOwnProperty(k) && typeof PicklistForm.prototype[k] === "function" && k !== "constructor") {
            PicklistForm.prototype[k] = PicklistForm.prototype[k].bind(this);
        }
    }

    // initialize fields that will be populated during first "open"
    this._currentFilterValue = null;
    this._currentOptions = null;
    this._isTyping = false;
    this._viewModel = null;
    this._savedFormState = null;
    this._searchStreamSubscription = null;

    // set container data binding
    // must be done here because we are not able to provide
    // this via the Razor file
    var containerDataBind = this.$form.attr("data-bind");
    this.$form.attr("data-bind", (containerDataBind ? containerDataBind + ", " : "") + "css: resolveContainerCssWithState()");
}


/**
 * Open this picklist form and initialize the view model.
 *
 * @param {Object} options
 *      Options to initialize the grid and form.
 * @param {string} options.containerCss
 *      Extra CSS that will be applied to the form container.
 * @param {function} options.createItem
 *      Callback function that will be called when the 'Create Item' button is pressed.
 * @param {Object} options.$element
 *      jQuery element to restore focus to after closing the form.
 * @param {number} options.filterDebounceTimeout
 *      Debounce time in milliseconds for filtered queries.
 * @param {string} options.filterValue
 *      Initial value for the filter input.
 * @param {boolean} options.hideFilter
 *      If true, will hide the filter input using CSS.
 * @param {Boolean} options.multiSelect
 *      Whether to put the form in multi-select mode (default: false).
 * @param {Function} options.openSelectedItem
 *      Callback function that will be called when the 'Open Item' button is pressed.
 * @param {Function} options.previewInit
 *      Function that returns a view model for the preview pane.
 * @param {Object[]} options.selectedItems
 *      Item(s) that represent the initial selection.
 * @param {Boolean} options.skipOpen
 *      Skip this forms open and use the default open.
 * @param {Object} options.text
 *      Overrides for texts and labels (see PicklistFormViewModel.prototype._textDefaults).
 * @param {Function} options.value
 *      Callback function that will receive the result when the form is accepted.
 * @param {Object} options.view
 *      (deprecated - define grid options as part of picklist data) Custom view to define the grid.
 * @return {Object}
 *      Instance of this picklist form.
 */
PicklistForm.prototype.open = function openPicklistForm(options) {
    if (typeof options !== "object") { throw new Error("PicklistForm.open requires options"); }

    var keymap = {
        "ctrl+space": R.partial(this._performGridAction, ["select", false]),
        "down": R.partial(this._performGridAction, ["movenext", false]),
        "enter": this._accept,
        "end": R.partial(this._performGridAction, ["moveend", false]),
        "esc": this._cancel,
        "home": R.partial(this._performGridAction, ["movehome", false]),
        "pagedown": R.partial(this._performGridAction, ["pagedown", false]),
        "pageup": R.partial(this._performGridAction, ["pageup", false]),
        "shift+down": R.partial(this._performGridAction, ["selectnext", false]),
        "shift+end": R.partial(this._performGridAction, ["selectend", false]),
        "shift+home": R.partial(this._performGridAction, ["selecthome", false]),
        "shift+up": R.partial(this._performGridAction, ["selectprevious", false]),
        "shift+tab": null,
        "space": R.partial(this._performGridAction, ["select", true]),
        "tab": null,
        "up": R.partial(this._performGridAction, ["moveprevious", false])
    };

    var optionsWithShortcuts = R.merge(options, {
        // set every action to 'null' for the keys defined in keymap;
        // this will prevent the default SlickGrid action and use the above keymap
        shortcutActions: R.map(R.always(null), keymap)
    });

    // initialize keymap
    // must be done before calling "_originalOpen" to ensure
    // keymaps for child forms are deactivated in correct order
    if (!this.keymap) {
        this.keymap = this.page.keymap = this.id;
        pli.keys.registerKeymap(this.keymap, keymap);
    }

    // reset viewmodel to use the specified options
    // on first open, also create + bind viewmodel;
    // Must be done before call to "_originalOpen"
    // so that the containerCss is correct and Forms.js
    // can properly position the newly opened form
    if (this._viewModel) {
        // save the current form state before resetting
        // so that when "_originalOpen" calls "getFormState"
        // it will get the form state for the currently
        // opened picklist and not the newly opened one
        // (re-entrancy)
        this._savedFormState = this._getFormState();

        this._viewModel.reset(optionsWithShortcuts);
    } else {
        // initialize viewmodel
        this._viewModel = this.createViewModel(this._originalClose);
        this._viewModel.reset(optionsWithShortcuts); // it is important to reset viewmodel before applying bindings so that all bound properties are fully defined

        this._viewModel.filter.subscribe(this._onViewModelFilterChanged, this);

        ko.applyBindings(this._viewModel, this.$form[0]); // knockout expects plain DOM element, unwrap jQuery array to get it
    }

    // show picklist form on screen (unless skipped)
    // keep in mind that Forms.js open ("_originalOpen")
    // will call "getFormState" if picklist form
    // is already opened (re-entrancy);
    // expects return from "getFormState" to be that
    // of the current opened picklist form, not the
    // new one that is being opened
    if (!options.skipOpen) {
        this._originalOpen();
    }

    // clear the saved form state
    this._savedFormState = null;

    // if options specifies an element associated
    // with this picklist (i.e. an input control where
    // accepted values will go), make sure it is focused
    // before showing picklist form (it might not be
    // focused when user clicks the picklist button
    // instead of using the F3 keyboard shortcut);
    // Forms.js will remember the focused element
    // when opening form, and will restore focus
    // to it when the form is closed
    if (options.$element) {
        options.$element.focus();
    }

    // remember options controlling grid behaviour
    this._currentFilterValue = optionsWithShortcuts.filterValue || "";
    this._currentOptions = optionsWithShortcuts;
    this._isTyping = false;

    if (this._searchStreamSubscription !== null) { this._searchStreamSubscription.unsubscribe(); }
    this._searchStreamSubscription = this._viewModel.searchStream.subscribe(this._onNewSearchResult.bind(this));

    return this;
};


/**
 * Close the picklist form
 * @return {Object}
 *      Instance of Picklistform
 */
PicklistForm.prototype.close = function closePicklistForm() {
    this._cancel();
    return this;
};


/**
 * Extract current state of picklist form (enables re-entrancy).
 * Will be called by Forms.js when picklist form is opened
 * as direct or indirect modal child of another picklist form.
 * @return {Object}
 *      The current state of the form
 */
PicklistForm.prototype.getFormState = function getPicklistFormState() {
    // if the form state was saved in the call to open,
    // restore from that save rather than creating new
    // data
    if (this._savedFormState) {
        return this._savedFormState;
    }

    return this._getFormState();
};


/**
 * Restore picklist form state to the specified value (enables re-entrancy).
 * Will be called by Forms.js when a child picklist form is closed.
 * @param {Object} formState
 *      State to restore
 */
PicklistForm.prototype.loadFormState = function loadPicklistFormState(formState) {
    if (formState) {
        this.open(formState);
        this.page.$focus = formState.$focus || null;
        this.$form.css("left", formState.positionLeft);
        this.$form.css("top", formState.positionTop);
    }
};


/**
 * Accept currently selected grid items.
 * If no items are selected or no grid has been created, does nothing.
 */
PicklistForm.prototype._accept = function acceptPicklistSelection() {
    this._cleanup();
    return (this._viewModel ? this._viewModel.accept() : false);
};


/**
 * Cancel this picklist form (close it).
 */
PicklistForm.prototype._cancel = function acceptPicklistSelection() {
    this._cleanup();
    return (this._viewModel ? this._viewModel.cancel() : false);
};


/**
 * Dispose any subscriptions held by the picklist form.
 */
PicklistForm.prototype._cleanup = function disposePicklistFormSubscriptions() {
    if (this._searchStreamSubscription !== null) {
        this._searchStreamSubscription.unsubscribe();
        this._searchStreamSubscription = null;
    }
};


/**
 * Extract current state of picklist form (enables re-entrancy).
 * @return {Object}
 *      The current state of the form
 */
PicklistForm.prototype._getFormState = function getPicklistFormState() {
    if (!this._viewModel) { return null; }

    var formState = R.merge({}, this._currentOptions);
    formState.$element = null;
    formState.$focus = this.page.$focus;
    formState.filterValue = this._currentFilterValue;
    formState.positionLeft = this.$form.css("left");
    formState.positionTop = this.$form.css("top");
    formState.selectedItems = this._viewModel.getSelectedRowsAsItems();
    formState.skipOpen = true;

    return formState;
};


/**
 * React to "_viewModel.searchStream" change notifications.
 */
PicklistForm.prototype._onNewSearchResult = function onPicklistNewSearchResult(result) {
    var filterValue = result.request;

    // after the throttled timeout, we are no longer typing
    this._isTyping = false;

    if (filterValue !== this._currentFilterValue) {
        this._currentFilterValue = filterValue;
    }
};


/**
 * React to "_viewModel.filter" change notifications.
 */
PicklistForm.prototype._onViewModelFilterChanged = function onPicklistFilterChanged() {
    // before the throttled timeout, any change counts as 'typing'
    this._isTyping = true;
};


/**
 * Performs a named grid action.
 * @param {string} action
 *      Name of the action to perform.
 * @param {boolean} checkIsTyping
 *      If true, will check the state of _isTyping before performing the grid action.
 */
PicklistForm.prototype._performGridAction = function performPicklistGridAction(action, checkIsTyping) {
    if (!this._viewModel.grid) {
        return true;
    }

    // multiselect
    if (this._viewModel.grid.options().multiSelect) {
        if (!checkIsTyping || !this._isTyping) {
            this._viewModel.grid.apply({ action: action });
        }

        // allow events to bubble if we think the user is typing
        // this is necessary for 'space' key to produce a space character
        return this._isTyping;
    }

    // single select
    if (!checkIsTyping) { // always allow 'space' in single-select
        this._viewModel.grid.apply({ action: action });
    }
    return checkIsTyping;
};


/**
 * Uniquely identify this singleton instance.
 * Useful during debugging and when checking dependencies.
 * @type {String}
 */
PicklistForm.prototype.type = "Picklist form (PLI navigation form)";


//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Initialization
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

/**
 * Upgrades the registered picklist form (created by Forms.js) to a fully functional one.
 *
 * @param createViewModel
 *     Picklist viewmodel initializer.
 */
function registerPicklistForm(createViewModel) {
    if (typeof pli.forms.registry.picklistForm !== "object") {
        throw new Error("Form \"picklistForm\" has not been registered in \"Pli.forms.registry\" (did you call \"CreatePicklistForm\" in Razor?)");
    }

    pli.forms.registry.picklistForm = new PicklistForm(pli.forms.registry.picklistForm);
    pli.forms.registry.picklistForm.createViewModel = createViewModel;
}

module.exports = {
    PicklistForm: PicklistForm,
    registerPicklistForm: registerPicklistForm
};




//////////////////
// WEBPACK FOOTER
// ./~/pli/forms-knockoutcontrols-picklist/picklistForm.js
// module id = 406
// module chunks = 49